"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import * as Sentry from "@sentry/react"

import { useUser, useUserActions, User, UserKycStatus } from "@/api/users"
import KycProcessScreen from "@/onboarding/components/KycProcessScreen"
import { createBridgeApiRetryHandler, parseOnboardingUrlParams } from "@/onboarding/onboarding.utils"

interface BridgeApiState {
  tosStatus: string | null
  kycStatus: string | null
  kycLink: string | null
  lastChecked: number | null
  error: string | null
}

interface BridgeApiResponse {
  tosStatus: string | null
  kycStatus: string | null
  kycLink: string | null
}

const INITIAL_BRIDGE_STATE: BridgeApiState = {
  tosStatus: null,
  kycStatus: null,
  kycLink: null,
  lastChecked: null,
  error: null,
}

type KycFlowMode = "initial" | "post-tos"

const logKycDebug = (...payload: unknown[]) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[KycScreen]", ...payload)
  }
}

const logKycError = (...payload: unknown[]) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error("[KycScreen]", ...payload)
  }
}

export default function KycScreen() {
  const { user, isLoading: isUserLoading, mutate: mutateUser } = useUser()
  const {
    startKycVerification,
    updateKycVerification,
    checkAndRefreshUserKycStatusByCustomerId,
    checkKycTosStatus,
  } = useUserActions()

  const navigate = useNavigate()
  const location = useLocation()

  const [isLoadingKycProcess, setLoadingKycProcess] = useState(false)
  const [bridgeApiState, setBridgeApiState] = useState<BridgeApiState>(INITIAL_BRIDGE_STATE)
  const [isTosDialogOpen, setIsTosDialogOpen] = useState(true)

  const forcedFromFeature = useMemo(() => {
    const state = location.state as Record<string, unknown> | null | undefined
    if (!state || typeof state !== "object") {
      return false
    }

    return state.forceRedirect === true || state.forceKyc === true
  }, [location.state])

  const actionInFlightRef = useRef(false)
  const isBridgeApiCallInProgressRef = useRef(false)
  const lastActionRef = useRef<string | null>(null)
  const unmountedRef = useRef(false)

  useEffect(() => {
    unmountedRef.current = false
    // Reset loading state on mount to handle React strict mode double mounting
    setLoadingKycProcess(false)
    
    return () => {
      unmountedRef.current = true
      // Ensure loading state is cleared on unmount
      setLoadingKycProcess(false)
    }
  }, [])

  const mergeBridgeApiState = useCallback(
    (
      update:
        | Partial<BridgeApiState>
        | ((prev: BridgeApiState) => BridgeApiState),
    ) => {
      setBridgeApiState((prev) => {
        const nextState =
          typeof update === "function"
            ? update(prev)
            : {
                ...prev,
                ...update,
              }
        return nextState
      })
    },
    [],
  )

  const checkBridgeStatusWithRetry = useCallback(
    async (retries = 3): Promise<BridgeApiResponse | null> => {
      logKycDebug("checkBridgeStatusWithRetry called", { retries, bridgeId: user?.bridgeId, inProgress: isBridgeApiCallInProgressRef.current })
      
      if (!user?.bridgeId || isBridgeApiCallInProgressRef.current) {
        logKycDebug("checkBridgeStatusWithRetry: early return", { noBridgeId: !user?.bridgeId, inProgress: isBridgeApiCallInProgressRef.current })
        return null
      }

      isBridgeApiCallInProgressRef.current = true
      try {
        const retryHandler = createBridgeApiRetryHandler(
          checkKycTosStatus,
          mergeBridgeApiState,
          user.bridgeId,
          "KycScreen.checkBridgeStatusWithRetry",
        )
        logKycDebug("checkBridgeStatusWithRetry: executing retry handler")
        const result = await retryHandler(retries)
        logKycDebug("checkBridgeStatusWithRetry: result", result)
        return result
      } finally {
        isBridgeApiCallInProgressRef.current = false
        logKycDebug("checkBridgeStatusWithRetry: cleanup complete")
      }
    },
    [user?.bridgeId, checkKycTosStatus, mergeBridgeApiState],
  )

  const handleTosRedirectBack = useCallback(async () => {
    logKycDebug("handleTosRedirectBack: iframe redirected back to our app")
    
    // Check current Bridge status to see what happened
    const bridgeResp = await checkBridgeStatusWithRetry()
    
    if (bridgeResp) {
      // Update local state with latest Bridge data
      mergeBridgeApiState({
        tosStatus: bridgeResp.tosStatus,
        kycStatus: bridgeResp.kycStatus,
        kycLink: bridgeResp.kycLink,
        lastChecked: Date.now(),
        error: null
      })
      
      // If KYC is approved or rejected, we're done - UI will show appropriate screen
      if (bridgeResp.kycStatus === "approved" || bridgeResp.kycStatus === "rejected") {
        logKycDebug("handleTosRedirectBack: KYC completed with status", bridgeResp.kycStatus)
        return
      }
      
      // If KYC status is still pending/not_started, reopen the KYC link
      if (bridgeResp.kycLink) {
        logKycDebug("handleTosRedirectBack: KYC not completed, reopening link", bridgeResp.kycLink)
        window.open(bridgeResp.kycLink, "_blank", "noopener,noreferrer")
      } else {
        logKycDebug("handleTosRedirectBack: No KYC link available after redirect")
      }
    } else {
      logKycError("handleTosRedirectBack: failed to check Bridge status after redirect")
    }
  }, [checkBridgeStatusWithRetry, mergeBridgeApiState])

  const redirectDestination = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const next = params.get("next")
    return next && next.trim() !== "" ? next : "/"
  }, [location.search])

  const cleanupActionParam = useCallback(() => {
    const params = new URLSearchParams(location.search)
    params.delete("action")
    const search = params.toString()
    navigate(location.pathname + (search ? `?${search}` : ""), { replace: true })
    lastActionRef.current = null
  }, [location.pathname, location.search, navigate])

  useEffect(() => {
    const { action } = parseOnboardingUrlParams(location.search)
    if (!action || actionInFlightRef.current || action === lastActionRef.current) {
      return
    }

    actionInFlightRef.current = true
    lastActionRef.current = action

    const run = async () => {
      setLoadingKycProcess(true)
      try {
        if (action === "verify") {
          await updateKycVerification()
          await mutateUser()
        } else if (action === "kyc-check") {
          await checkAndRefreshUserKycStatusByCustomerId()
          await mutateUser()
        }
        await checkBridgeStatusWithRetry()
      } catch (error) {
        Sentry.captureException(error as Error, {
          extra: { context: "KycScreen.handleAction", action },
        })
      } finally {
        if (!unmountedRef.current) {
          setLoadingKycProcess(false)
          cleanupActionParam()
          actionInFlightRef.current = false
        }
      }
    }

    void run()
  }, [
    location.search,
    cleanupActionParam,
    mutateUser,
    updateKycVerification,
    checkAndRefreshUserKycStatusByCustomerId,
    checkBridgeStatusWithRetry,
  ])

  useEffect(() => {
    if (
      user?.bridgeId &&
      bridgeApiState.lastChecked === null &&
      !isBridgeApiCallInProgressRef.current &&
      !isLoadingKycProcess
    ) {
      void checkBridgeStatusWithRetry()
    }
  }, [
    user?.bridgeId,
    bridgeApiState.lastChecked,
    isLoadingKycProcess,
    checkBridgeStatusWithRetry,
  ])

  const startOrContinueKyc = useCallback(
    async (mode: KycFlowMode) => {
      logKycDebug("startOrContinueKyc called", { mode, isLoadingKycProcess })
      
      if (isLoadingKycProcess) {
        logKycDebug("startOrContinueKyc: already loading, returning early")
        return
      }

      const shouldOpenInNewTab = mode === "post-tos"
      let newTabHandle: Window | null = null
      let openedInNewTab = false
      
      logKycDebug("startOrContinueKyc: setup complete", { shouldOpenInNewTab })

      if (shouldOpenInNewTab) {
        newTabHandle = window.open("", "_blank", "noopener,noreferrer")
      }

      const resolveTargetWindow = () => {
        if (newTabHandle && !newTabHandle.closed) {
          return newTabHandle
        }

        const fallbackWindow = window.open("", "_blank", "noopener,noreferrer")
        if (fallbackWindow) {
          newTabHandle = fallbackWindow
        }
        return fallbackWindow
      }

      const closePendingNewTab = () => {
        if (shouldOpenInNewTab && newTabHandle && !openedInNewTab && !newTabHandle.closed) {
          newTabHandle.close()
          newTabHandle = null
        }
      }

      const openKycLink = (targetLink: string) => {
        if (!targetLink) {
          return
        }

        if (shouldOpenInNewTab) {
          const targetWindow = resolveTargetWindow()
          if (targetWindow) {
            targetWindow.location.href = targetLink
            openedInNewTab = true
            return
          }
        }

        window.location.href = targetLink
      }

      setLoadingKycProcess(true)
      try {
        if (mode === "post-tos") {
          logKycDebug("startOrContinueKyc: TOS accepted, immediately opening KYC link")
          
          // Update state to reflect TOS approval
          mergeBridgeApiState((prev) => ({
            ...prev,
            tosStatus: "approved",
            lastChecked: null,
            error: null,
          }))

          // Get KYC link from current state or user data and open immediately
          const kycLink = bridgeApiState.kycLink || user?.bridgeKycLink
          if (kycLink) {
            logKycDebug("startOrContinueKyc: opening KYC link immediately after TOS", { kycLink })
            openKycLink(kycLink)
            return
          } else {
            logKycDebug("startOrContinueKyc: no KYC link available, fetching from Bridge")
            // If no KYC link cached, fetch it quickly
            const bridgeResp = await checkBridgeStatusWithRetry(1)
            if (bridgeResp?.kycLink) {
              logKycDebug("startOrContinueKyc: got KYC link from Bridge, opening")
              openKycLink(bridgeResp.kycLink)
              return
            }
          }
        }

        if (mode === "initial") {
          const params = new URLSearchParams(location.search)
          params.set("action", "kyc-check")
          const redirectUrl = `${window.location.origin}${location.pathname}?${params.toString()}`

          logKycDebug("startOrContinueKyc: starting KYC verification")
          await startKycVerification(redirectUrl)
          await mutateUser()
          mergeBridgeApiState({ lastChecked: null, error: null })
          
          // Always check Bridge API status first to determine next step
          if (user?.bridgeId) {
            logKycDebug("startOrContinueKyc: bridgeId exists, checking Bridge status")
            const bridgeResp = await checkBridgeStatusWithRetry()
            logKycDebug("startOrContinueKyc: Bridge status for existing user", bridgeResp)
            
            if (bridgeResp) {
              // Update local state with Bridge API response
              mergeBridgeApiState({
                tosStatus: bridgeResp.tosStatus,
                kycStatus: bridgeResp.kycStatus,
                kycLink: bridgeResp.kycLink,
                lastChecked: Date.now(),
                error: null
              })
              
              // If KYC is already approved - we're done! Let UI show success
              if (bridgeResp.kycStatus === "approved") {
                logKycDebug("startOrContinueKyc: KYC already approved, showing success screen")
                return
              }
              
              // If KYC is rejected - we're done! Let UI show rejection message  
              if (bridgeResp.kycStatus === "rejected") {
                logKycDebug("startOrContinueKyc: KYC rejected, showing rejection screen")
                return
              }
              
              // For any other status (pending, null, etc.) - open KYC link if available
              if (bridgeResp.kycLink) {
                logKycDebug("startOrContinueKyc: KYC needs completion, opening link", {
                  tosStatus: bridgeResp.tosStatus,
                  kycStatus: bridgeResp.kycStatus
                })
                openKycLink(bridgeResp.kycLink)
                return
              }
              
              // No KYC link available but status indicates need - show TOS if needed
              logKycDebug("startOrContinueKyc: No KYC link, checking TOS flow", {
                tosStatus: bridgeResp.tosStatus,
                kycStatus: bridgeResp.kycStatus
              })
              return
            }
          } else {
            logKycDebug("startOrContinueKyc: no bridgeId yet, TOS acceptance required first")
            return // Let the UI handle showing TOS iframe
          }
        }

        logKycError("startOrContinueKyc: Reached unexpected flow state")
        throw new Error("Unexpected KYC flow state")
      } catch (error) {
        logKycError("startOrContinueKyc: error occurred", error)
        Sentry.captureException(error as Error, {
          extra: { context: "KycScreen.startOrContinueKyc", mode },
        })
        if (user?.bridgeKycLink) {
          logKycDebug("startOrContinueKyc: using fallback bridge KYC link")
          openKycLink(user.bridgeKycLink)
        }
      } finally {
        logKycDebug("startOrContinueKyc: cleanup starting", { unmounted: unmountedRef.current })
        closePendingNewTab()
        if (!unmountedRef.current) {
          logKycDebug("startOrContinueKyc: setting loading to false")
          setLoadingKycProcess(false)
        } else {
          logKycDebug("startOrContinueKyc: component unmounted, skipping state update")
        }
      }
    },
    [
      isLoadingKycProcess,
      location.pathname,
      location.search,
      startKycVerification,
      mutateUser,
      mergeBridgeApiState,
      checkBridgeStatusWithRetry,
      bridgeApiState,
      user,
    ],
  )

  const handleNavigateHome = useCallback(() => {
    navigate(redirectDestination, { replace: true })
  }, [navigate, redirectDestination])

  useEffect(() => {
    if (user?.kycStatus === UserKycStatus.SUCCESS) {
      logKycDebug("useEffect: syncing user KYC success to bridge state")
      mergeBridgeApiState((prev) => ({
        ...prev,
        kycStatus: "approved",
      }))
    } else if (user?.kycStatus === UserKycStatus.FAILED) {
      logKycDebug("useEffect: syncing user KYC failure to bridge state") 
      mergeBridgeApiState((prev) => ({
        ...prev,
        kycStatus: "rejected",
      }))
    }
  }, [user?.kycStatus, mergeBridgeApiState])



  if (isUserLoading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-sm text-gray-600">Loading account details...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-2xl">
        <div className="bg-white shadow-lg border border-gray-200 rounded-lg px-6 sm:px-8 py-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 text-center mb-6">
            Identity Verification
          </h1>
          {forcedFromFeature && (
            <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              To continue, please complete identity verification. Once finished, we'll take you back to what you were doing.
            </div>
          )}
          <KycProcessScreen
            user={user as User}
            startKyc={() => {
              logKycDebug("KYC Screen: startKyc called (initial mode)")
              return startOrContinueKyc("initial")
            }}
            onAcceptTosAndStartKyc={() => {
              logKycDebug("KYC Screen: onAcceptTosAndStartKyc called (post-tos mode)")
              return startOrContinueKyc("post-tos")
            }}
            isLoadingKyc={isLoadingKycProcess}
            onNext={handleNavigateHome}
            isKycRequired
            bridgeTosStatus={bridgeApiState.tosStatus}
            bridgeKycStatus={bridgeApiState.kycStatus}
            bridgeKycLink={bridgeApiState.kycLink}
            successCtaLabel="Go to Dashboard"
            isTosDialogOpen={isTosDialogOpen}
            onTosDialogOpenChange={(open) => setIsTosDialogOpen(open)}
            onTosRedirectBack={handleTosRedirectBack}
          />
        </div>
      </div>
    </div>
  )
}
